import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useGetAssemblies } from './hooks/useGetAssemblies';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { useInsightsPageParams } from '@/hooks';
import { ReviewScreenHeader } from './ReviewScreenHeader';
import { TabNavigation } from './TabNavigation';
import { useBulkAssemblyData } from './hooks/useBulkAssemblyData';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { ImagePreviewDrawer } from './ImagePreviewDrawer';

export const ReviewScreen = () => {
  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(state.selectedAssemblyId);
  const selectedPoiId = useAtomValue(poiState.selectedPointOfInterestId);
  const setSelectedPoiId = useSetAtom(poiState.selectedPointOfInterestId);
  const setSelectedPoi = useSetAtom(poiState.selectedPointOfInterest);
  const setSelectedAssemblyName = useSetAtom(state.selectedAssemblyName);
  const setSelectedReviewPoiFilters = useSetAtom(state.selectedReviewPoiFilters);
  const setSelectedReviewPaintRegionIds = useSetAtom(state.selectedReviewPaintRegionIds);
  const setSelectedReviewWorkpackIds = useSetAtom(state.selectedReviewWorkpackIds);
  const setSelectedReviewPoiTypeId = useSetAtom(state.selectedReviewPoiTypeId);
  const setSelectedReviewPoiSearchTerm = useSetAtom(state.reviewPoiSearchTerm);
  const setSelectedReviewPoiTypes = useSetAtom(state.selectedReviewPoiTypes);
  const setIsAllReviewPointsOfInterestSelected = useSetAtom(
    state.isAllReviewPointsOfInterestSelected
  );
  const setSelectedReviewPointsOfInterest = useSetAtom(state.selectedReviewPointsOfInterest);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navbarHeight = useNavbarHeight();
  const { clearAssembly, clearPoi } = useInsightsPageParams();
  /** Avoids re-applying `poi` from a stale URL while `clearPoi` / router search is updating (e.g. preview back). */
  const suppressPoiUrlHydrationRef = useRef(false);
  useGetAssemblies();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const assemblyIdFromUrl = params.get('selectedAssemblyId');
    const assemblyNameFromUrl = params.get('selectedAssemblyName');
    if (assemblyIdFromUrl && assemblyNameFromUrl) {
      setSelectedAssemblyId(assemblyIdFromUrl);
      setSelectedAssemblyName(assemblyNameFromUrl);
    }
    setSelectedReviewPointsOfInterest([]);
    setIsAllReviewPointsOfInterestSelected(false);
    setSelectedReviewPoiTypeId(undefined);
    setSelectedReviewPoiTypes([]);
  }, [
    setSelectedReviewPointsOfInterest,
    setIsAllReviewPointsOfInterestSelected,
    setSelectedReviewPoiTypeId,
    setSelectedReviewPoiTypes,
    setSelectedAssemblyId,
    setSelectedAssemblyName,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const poiFromUrl = params.get('poi');
    if (suppressPoiUrlHydrationRef.current) {
      if (!poiFromUrl) {
        suppressPoiUrlHydrationRef.current = false;
      }
      return;
    }
    if (poiFromUrl && poiFromUrl !== selectedPoiId) {
      setSelectedPoi(undefined);
      setSelectedPoiId(poiFromUrl);
    }
  }, [selectedPoiId, setSelectedPoiId, setSelectedPoi]);

  const beginClosingPoiPreview = useCallback(() => {
    suppressPoiUrlHydrationRef.current = true;
  }, []);

  const handleClose = useCallback(() => {
    suppressPoiUrlHydrationRef.current = true;
    setDrawerOpen(false);
    setSelectedAssemblyId(undefined);
    setSelectedAssemblyName(undefined);
    setSelectedReviewPoiFilters({});
    setSelectedReviewPaintRegionIds([]);
    setSelectedReviewWorkpackIds([]);
    setSelectedReviewPoiTypeId(undefined);
    setSelectedReviewPoiSearchTerm('');
    setIsAllReviewPointsOfInterestSelected(false);
    setSelectedReviewPointsOfInterest([]);
    setSelectedReviewPoiTypes([]);
    setSelectedPoiId(undefined);
    clearAssembly();
    clearPoi();
  }, [
    setSelectedAssemblyId,
    setSelectedAssemblyName,
    setSelectedReviewPoiFilters,
    setSelectedReviewPaintRegionIds,
    setSelectedReviewWorkpackIds,
    setSelectedReviewPoiTypeId,
    setSelectedReviewPoiSearchTerm,
    setIsAllReviewPointsOfInterestSelected,
    setSelectedReviewPointsOfInterest,
    setSelectedReviewPoiTypes,
    setSelectedPoiId,
    clearAssembly,
    clearPoi,
  ]);

  useEffect(() => {
    setDrawerOpen(!!selectedAssemblyId);
  }, [selectedAssemblyId]);

  useBulkAssemblyData({ assemblyIds: selectedAssemblyId ? [selectedAssemblyId] : [] });

  return (
    <>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleClose}
        // `disableEnforceFocus` lets inputs in PoiEditingForms (mounted outside this
        // Drawer's Modal) receive focus when edit actions are triggered.
        ModalProps={{ disableEnforceFocus: true }}
        PaperProps={{
          sx: { width: '70vw', backgroundColor: '#fff' },
        }}
        transitionDuration={300}
      >
        <Stack
          sx={{ paddingTop: `${navbarHeight}px`, overflowY: 'auto', height: '100%' }}
          id="review-panel"
        >
          {selectedAssemblyId && (
            <>
              <ReviewScreenHeader handleClose={handleClose} />
              <TabNavigation />
            </>
          )}
        </Stack>
      </Drawer>
      <ImagePreviewDrawer
        handleCloseAll={handleClose}
        onBeginCloseFromPreview={beginClosingPoiPreview}
      />
    </>
  );
};
